import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import logo from '../../assets/logo.png';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://apicabaza.creago.be/rest/cabaza';

const ResetPasswordPage = ({ language = 'fr' }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';

    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const t = {
        fr: {
            title: 'Nouveau mot de passe',
            subtitle: 'Choisissez un mot de passe sécurisé pour votre compte.',
            password: 'Nouveau mot de passe',
            passwordConfirm: 'Confirmer le mot de passe',
            submit: 'Réinitialiser mon mot de passe',
            loading: 'Réinitialisation...',
            back: 'Retour à la connexion',
            successTitle: 'Mot de passe modifié !',
            successMsg: 'Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter.',
            goLogin: 'Se connecter',
            invalidToken: 'Ce lien est invalide ou a expiré. Veuillez faire une nouvelle demande.',
            noToken: 'Lien invalide. Veuillez utiliser le lien reçu par email.',
            minLength: 'Le mot de passe doit contenir au moins 6 caractères.',
            noMatch: 'Les mots de passe ne correspondent pas.',
        },
        en: {
            title: 'New Password',
            subtitle: 'Choose a secure password for your account.',
            password: 'New password',
            passwordConfirm: 'Confirm password',
            submit: 'Reset my password',
            loading: 'Resetting...',
            back: 'Back to login',
            successTitle: 'Password updated!',
            successMsg: 'Your password has been reset successfully. You can now log in.',
            goLogin: 'Sign in',
            invalidToken: 'This link is invalid or has expired. Please make a new request.',
            noToken: 'Invalid link. Please use the link received by email.',
            minLength: 'Password must be at least 6 characters.',
            noMatch: 'Passwords do not match.',
        }
    };

    const texts = t[language] || t.fr;

    useEffect(() => {
        if (!token) setError(texts.noToken);
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (password.length < 6) {
            setError(texts.minLength);
            return;
        }
        if (password !== passwordConfirm) {
            setError(texts.noMatch);
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/peoples/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, password }),
            });
            const data = await res.json();
            if (data.success) {
                setSuccess(true);
            } else {
                setError(data.message || texts.invalidToken);
            }
        } catch {
            setError(texts.invalidToken);
        } finally {
            setLoading(false);
        }
    };

    const strengthScore = () => {
        if (!password) return 0;
        let score = 0;
        if (password.length >= 6) score++;
        if (password.length >= 10) score++;
        if (/[A-Z]/.test(password)) score++;
        if (/[0-9]/.test(password)) score++;
        if (/[^a-zA-Z0-9]/.test(password)) score++;
        return score;
    };

    const strengthColor = ['', 'bg-red-400', 'bg-orange-400', 'bg-yellow-400', 'bg-emerald-400', 'bg-green-500'];
    const strengthLabel = { fr: ['', 'Très faible', 'Faible', 'Moyen', 'Fort', 'Très fort'], en: ['', 'Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'] };
    const score = strengthScore();

    return (
        <div className="min-h-screen bg-white flex items-center justify-center px-4">
            <div className="max-w-md w-full">

                <button
                    onClick={() => navigate('/login')}
                    className="mb-6 flex items-center gap-2 text-cabaza-purple hover:text-cabaza-pink transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                    {texts.back}
                </button>

                <div className="bg-white rounded-2xl shadow-xl p-8">
                    <div className="text-center mb-8">
                        <img src={logo} alt="Cabaza Logo" className="h-20 mx-auto mb-4" />
                        <h1 className="text-2xl font-bold text-cabaza-purple">{texts.title}</h1>
                        {!success && <p className="mt-2 text-sm text-gray-500">{texts.subtitle}</p>}
                    </div>

                    {success ? (
                        <div className="text-center">
                            <div className="flex justify-center mb-4">
                                <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center">
                                    <CheckCircle className="w-8 h-8 text-green-500" />
                                </div>
                            </div>
                            <h2 className="text-lg font-semibold text-gray-800 mb-2">{texts.successTitle}</h2>
                            <p className="text-sm text-gray-500 mb-6">{texts.successMsg}</p>
                            <button
                                onClick={() => navigate('/login')}
                                className="w-full py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-cabaza-purple to-cabaza-pink hover:opacity-90 transition-opacity"
                            >
                                {texts.goLogin}
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-5">
                            {error && (
                                <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2">
                                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                                    <p className="text-red-800 text-sm">{error}</p>
                                </div>
                            )}

                            {/* Password */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    {texts.password}
                                </label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cabaza-purple/30 focus:border-cabaza-purple transition-colors"
                                        required
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(v => !v)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                                {/* Strength bar */}
                                {password.length > 0 && (
                                    <div className="mt-2">
                                        <div className="flex gap-1 mb-1">
                                            {[1,2,3,4,5].map(i => (
                                                <div
                                                    key={i}
                                                    className={`h-1.5 flex-1 rounded-full transition-all ${i <= score ? strengthColor[score] : 'bg-gray-100'}`}
                                                />
                                            ))}
                                        </div>
                                        <p className="text-xs text-gray-500">{(strengthLabel[language] || strengthLabel.fr)[score]}</p>
                                    </div>
                                )}
                            </div>

                            {/* Confirm */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    {texts.passwordConfirm}
                                </label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type={showConfirm ? 'text' : 'password'}
                                        value={passwordConfirm}
                                        onChange={e => setPasswordConfirm(e.target.value)}
                                        className={`w-full pl-10 pr-10 py-3 border rounded-xl focus:outline-none focus:ring-2 transition-colors ${
                                            passwordConfirm && password !== passwordConfirm
                                                ? 'border-red-300 focus:ring-red-200'
                                                : 'border-gray-200 focus:ring-cabaza-purple/30 focus:border-cabaza-purple'
                                        }`}
                                        required
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirm(v => !v)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                                {passwordConfirm && password !== passwordConfirm && (
                                    <p className="mt-1 text-xs text-red-500">{texts.noMatch}</p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={loading || !token}
                                className="w-full py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-cabaza-purple to-cabaza-pink hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? texts.loading : texts.submit}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
